import { Box, Button, Paper, Typography } from '@mui/material';
import type { Permission, SupportedLocale } from '@stm/contracts';
import { translate } from '@stm/i18n';
import { Link } from 'react-router-dom';
import { isFamilyView, navigationLabel, sectionIntroduction } from './navigation';
import { NavigationIcon } from './NavigationIcon';
import { hasFeature, type NavigationItem } from './routes';

export function HomePanel({
  locale,
  navigation,
  permissions,
}: {
  locale: SupportedLocale;
  navigation: readonly NavigationItem[];
  permissions: readonly Permission[];
}): React.JSX.Element {
  const familyView = isFamilyView(permissions);
  const tasks = navigation.filter(
    (item) => item.permission !== 'overview.view' && hasFeature(item.path, permissions),
  );
  return (
    <Box sx={{ mb: 4 }}>
      <Typography variant="h6" component="h2" gutterBottom>
        {translate(locale, 'home.tasks.title')}
      </Typography>
      <Typography color="text.secondary" variant="body2" sx={{ mb: 2.5 }}>
        {translate(locale, 'home.tasks.description')}
      </Typography>
      {tasks.length === 0 && (
        <Paper variant="outlined" sx={{ padding: 3 }}>
          {translate(locale, 'home.noTasks')}
        </Paper>
      )}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            sm: 'repeat(2, minmax(0, 1fr))',
            lg: 'repeat(3, minmax(0, 1fr))',
          },
          gap: 2,
        }}
      >
        {tasks.map((item) => (
          <Paper
            key={item.path}
            variant="outlined"
            sx={{
              padding: 2.5,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-start',
              transition: 'border-color 150ms',
              '&:hover': { borderColor: 'primary.main' },
            }}
          >
            <Box
              sx={{
                bgcolor: '#e8f3ef',
                color: 'primary.main',
                borderRadius: 2,
                width: 44,
                height: 44,
                display: 'grid',
                placeItems: 'center',
                mb: 2,
              }}
            >
              <NavigationIcon permission={item.permission} />
            </Box>
            <Typography variant="h6" component="h3" gutterBottom>
              {translate(locale, navigationLabel(item, familyView))}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2, flex: 1 }}>
              {translate(locale, sectionIntroduction(item.permission, familyView))}
            </Typography>
            <Button
              component={Link}
              to={item.path}
              variant="text"
              aria-label={`${translate(locale, 'home.openSection')}: ${translate(locale, navigationLabel(item, familyView))}`}
              sx={{ px: 0 }}
            >
              {translate(locale, 'home.openSection')}{' '}
              <Box component="span" aria-hidden="true" sx={{ ml: 1 }}>
                →
              </Box>
            </Button>
          </Paper>
        ))}
      </Box>
    </Box>
  );
}
